import React, { useEffect } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import { GroupBase, MenuProps, components as ReactSelectComponents } from 'react-select';
import PortalWrp, { PortalId } from '@creditflowresearch/uilib/Layout/PortalWrp';
import useMenuOuterStyle from './useMenuOuterStyle';

import styles from '../select.module.scss';

interface IProps<SOption, IsMulti extends boolean, Group extends GroupBase<SOption>> {
  className?: string,
  dataTest: string,
  selectWrapper: HTMLDivElement,
  isUsePortal?: boolean,
  bodyElement?: HTMLElement,
  scrollableParentElements?: HTMLElement[],
  getMenuOuterStyle?: (defaultStyle: () => React.CSSProperties, container: HTMLDivElement) => React.CSSProperties,
  ownProps: MenuProps<SOption, IsMulti, Group>,
}

const Menu = <SOption, IsMulti extends boolean, Group extends GroupBase<SOption>>({
  className,
  dataTest,
  isUsePortal,
  selectWrapper,
  bodyElement,
  getMenuOuterStyle,
  scrollableParentElements,
  ownProps: {
    children,
    ...otherProps
  },
}: IProps<SOption, IsMulti, Group>) => {
  const dataTestAttr = `${ dataTest }_options`;
  const {
    menuOuterStyle,
    getOuterStyle,
    setMenuOuterStyle,
  } = useMenuOuterStyle({ selectWrapper, bodyElement, getMenuOuterStyle });

  useEffect(() => {
    const updateOffsetScroll = throttle(() => setMenuOuterStyle(getOuterStyle()), 50);

    if (isUsePortal && scrollableParentElements) {
      scrollableParentElements.forEach((element) => {
        element?.addEventListener('scroll', updateOffsetScroll);
      });
    }

    return () => {
      if (isUsePortal && scrollableParentElements) {
        scrollableParentElements.forEach((element) => {
          element?.removeEventListener('scroll', updateOffsetScroll);
        });
      }
    };
  }, []);

  if (isUsePortal) {
    return (
      <PortalWrp portalId={ PortalId.PORTAL_OVERLAY_ID }>
        <div className={ styles.selectPortalEncapsulation }>
          <div
            className="Select-menu-outer"
            style={ menuOuterStyle }
          >
            <div className={ cn(['Select-menu', className]) }>
              <span data-test={ dataTestAttr }>
                <ReactSelectComponents.Menu { ...otherProps }>
                  { children }
                </ReactSelectComponents.Menu>
              </span>
            </div>
          </div>
        </div>
      </PortalWrp>
    );
  }

  return (
    <div data-test={ dataTestAttr } className={ cn(['Select-menu-wrapper', className]) }>
      <ReactSelectComponents.Menu { ...otherProps }>
        { children }
      </ReactSelectComponents.Menu>
    </div>
  );
};

export default Menu;
